import { Autocomplete, TextField, Typography } from '@mui/material';
import type { PlanDuration, PlanTrial } from '@vy/types';
import { isNotNullOrUndefined, parseNumber } from '@vy/utils';
import { isEqual } from 'lodash';
import React, { useMemo, useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { PlanSettingsSelectItem } from './PlanSettingsSelect';

type PlanSettingsType = PlanTrial | PlanDuration | null;

type PlanCreateAutoCompleteFieldProps<FormFields extends FieldValues> = {
  className?: string;
  control: Control<FormFields>;
  name: FieldPath<FormFields>;
  values: PlanSettingsSelectItem<PlanSettingsType | undefined>[];
  label: string;
  disabled?: boolean;
  defaultItem: PlanSettingsSelectItem<PlanSettingsType | undefined> | undefined;
  mapValueChange: (inputNumber: number) => PlanSettingsType | undefined;
};

export const PlanSettingsAutoCompleteField = <FormFields extends FieldValues>({
  control,
  mapValueChange,
  name,
  values,
  disabled,
  defaultItem,
}: PlanCreateAutoCompleteFieldProps<FormFields>) => {
  const [customDuration, setCustomDuration] =
    useState<PlanSettingsSelectItem<PlanSettingsType | undefined>>();

  const options = useMemo(() => getParsedDurationValues(customDuration, values), [customDuration, values]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: handleChange, ...field } }) => (
        <Autocomplete
          key={defaultItem?.value?.toString()}
          options={options}
          onClose={() => {
            setCustomDuration(undefined);
          }}
          onChange={(_, value) => {
            handleChange(value.value);
            setCustomDuration(value ?? undefined);
          }}
          disabled={disabled}
          value={options.find(({ value }) => isEqual(value, field.value)) ?? defaultItem}
          isOptionEqualToValue={(option, value) => isEqual(option.value, value.value)}
          disableClearable
          renderOption={(renderProps, option) => (
            <li className="flex items-center" {...renderProps}>
              <Typography>{option.label}</Typography>
            </li>
          )}
          forcePopupIcon
          renderInput={(params) => (
            <TextField
              {...field}
              {...params}
              size="small"
              value={customDuration ? customDuration.value : field.value}
              onChange={(e) => {
                const input = e.target.value;
                const inputNumber = parseNumber(input);
                if (isNotNullOrUndefined(inputNumber) && inputNumber > 0) {
                  const mappedValue = mapValueChange(inputNumber);
                  setCustomDuration({
                    value: mappedValue,
                    label: `${inputNumber} months`,
                  });
                }
              }}
              slotProps={{
                ...params.slotProps,
                input: params.slotProps.input
              }}
            />
          )}
        />
      )}
    />
  );
};

function getParsedDurationValues(
  customValue: PlanSettingsSelectItem<PlanSettingsType | undefined> | undefined,
  values: PlanSettingsSelectItem<PlanSettingsType | undefined>[],
) {
  const customAlreadyExist = values.find(({ value }) => isEqual(value, customValue?.value));

  if (!customValue) {
    return values;
  }

  return !customAlreadyExist ? [customValue, ...values] : values;
}
